import type { InstalledPlugin } from '@crowdex/plugin-contracts';
import { Button } from '@crowdex/ui/components/button';
import { Textarea } from '@crowdex/ui/components/textarea';
import { FIELD_GEOMETRY, fieldClass } from '@crowdex/ui/lib/surfaces';
import { ExternalLink, FileCode2, LoaderCircle, Save, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { type McpServerStatus, nativePluginClient } from '@/lib/plugins/plugin-native-client';
import { isPluginSourceConfigurable } from '@/lib/plugins-page/plugin-status';
import type { usePluginManagement } from '@/lib/plugins-page/use-plugin-management';
import { PluginSettingsForm } from './plugin-settings-form';

function McpServerDiagnostics({ plugin }: { plugin: InstalledPlugin }) {
  const [statuses, setStatuses] = useState<McpServerStatus[] | null>(null);
  const [loading, setLoading] = useState(false);
  const refresh = async () => {
    setLoading(true);
    try {
      const all = await nativePluginClient.listMcpServerStatuses();
      setStatuses(all.filter((status) => status.serverId.startsWith(`${plugin.name}.`)));
    } finally {
      setLoading(false);
    }
  };
  return (
    <section>
      <div className="mb-3 flex items-center gap-2">
        <ShieldCheck className="size-4 text-muted-foreground" />
        <h3 className="font-medium text-sm">MCP host</h3>
        <Button
          variant="outline"
          className="ml-auto rounded-lg border border-border bg-control px-2.5 py-1 text-xs disabled:opacity-50"
          disabled={loading}
          onClick={() => void refresh()}
          type="button"
        >
          {loading ? 'Checking…' : statuses === null ? 'Check servers' : 'Refresh'}
        </Button>
      </div>
      {statuses !== null && (
        <div className="space-y-2">
          {statuses.length === 0 && (
            <p className="rounded-xl border border-border bg-surface-panel p-3 text-muted-foreground text-xs">
              No reachable MCP server for this plugin.
            </p>
          )}
          {statuses.map((status) => (
            <div
              className="rounded-xl border border-border bg-surface-panel p-3"
              key={status.serverId}
            >
              <div className="flex items-center gap-2">
                <span
                  className={`size-2 rounded-full ${status.state === 'connected' ? 'bg-emerald-500' : status.state === 'error' ? 'bg-red-500' : 'bg-amber-500'}`}
                />
                <code className="min-w-0 flex-1 truncate text-xs">{status.serverId}</code>
                {status.state === 'error' && (
                  <Button
                    variant="outline"
                    className="rounded-lg border border-border bg-control px-2.5 py-1 text-xs"
                    onClick={() =>
                      void nativePluginClient
                        .reconnectMcpServer(status.serverId)
                        .then(() => refresh())
                    }
                    type="button"
                  >
                    Reconnect
                  </Button>
                )}
              </div>
              {status.error && <p className="mt-2 text-red-500 text-xs">{status.error}</p>}
              {status.discovery?.instructions && (
                <p className="mt-2 whitespace-pre-wrap text-muted-foreground text-xs">
                  {status.discovery.instructions}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

type Management = ReturnType<typeof usePluginManagement>;

/**
 * An installed plugin's settings, inside its page: how it behaves and its source. Its credentials are its accounts, connections of the hub.
 */
export function PluginConfiguration({
  plugin,
  management,
}: {
  plugin: InstalledPlugin;
  management: Management;
}) {
  const {
    error,
    loadSource,
    saveSource,
    setSourceContent,
    sourceContent,
    sourceLoading,
    sourcePath,
    sourceSaving,
  } = management;
  return (
    <div className="space-y-5">
      {error && (
        <div className="rounded-xl border border-destructive/25 bg-destructive/8 p-3 text-destructive text-sm">
          {error}
        </div>
      )}
      <PluginSettingsForm plugin={plugin} workspaceRoot={management.workspaceRoot} />
      {isPluginSourceConfigurable(plugin) && (
        <section>
          <div className="mb-3 flex items-center gap-2">
            <FileCode2 className="size-4 text-muted-foreground" />
            <div>
              <h3 className="font-medium text-sm">Discovered source</h3>
              <p className="mt-0.5 text-muted-foreground text-xs">
                Changes are validated and written directly to the original file.
              </p>
            </div>
          </div>
          <div className="overflow-hidden rounded-xl border border-border bg-surface-panel">
            <div className="border-border border-b p-3">
              <select
                className={fieldClass(FIELD_GEOMETRY, 'w-full font-mono')}
                onChange={(event) => void loadSource(plugin, event.target.value)}
                value={sourcePath ?? ''}
              >
                {plugin.sourceFiles?.map((source) => (
                  <option key={source.path} value={source.path}>
                    {source.kind} · {source.label}
                  </option>
                ))}
              </select>
              {sourcePath && (
                <p className="type-caption mt-2 break-all font-mono text-muted-foreground">
                  {sourcePath}
                </p>
              )}
            </div>
            <Textarea
              aria-label="Plugin source file"
              className="min-h-72 w-full resize-y bg-background p-4 font-mono text-xs leading-5 outline-none disabled:opacity-50"
              disabled={sourceLoading || sourceSaving}
              onChange={(event) => setSourceContent(event.target.value)}
              spellCheck={false}
              value={sourceLoading ? 'Loading…' : sourceContent}
            />
            <div className="flex justify-end border-border border-t p-3">
              <Button
                variant="default"
                className="type-ui flex h-control-md items-center gap-2 rounded-lg px-4 disabled:opacity-50"
                disabled={sourceLoading || sourceSaving || sourcePath === null}
                onClick={() => void saveSource(plugin)}
                type="button"
              >
                {sourceSaving ? (
                  <LoaderCircle className="size-4 animate-spin" />
                ) : (
                  <Save className="size-4" />
                )}
                Save source
              </Button>
            </div>
          </div>
        </section>
      )}
      {plugin.mcpServers.length > 0 && <McpServerDiagnostics plugin={plugin} />}
      {plugin.mcpSource && (
        <a
          className="inline-flex items-center gap-1 text-muted-foreground text-xs hover:text-foreground"
          href={plugin.mcpSource.url}
          rel="noreferrer"
          target="_blank"
        >
          {plugin.mcpSource.label} <ExternalLink className="size-3" />
        </a>
      )}
    </div>
  );
}
